import {
  appThemeApiRef,
  type AppTheme,
} from '@backstage/core-plugin-api';
import { useApi } from '@backstage/core-plugin-api';
import useObservable from 'react-use/esm/useObservable';
import { usePrefersDarkColorScheme } from './usePrefersDarkColorScheme';

function resolveTheme(
  themeId: string | undefined,
  shouldPreferDark: boolean,
  themes: AppTheme[],
): AppTheme | undefined {
  if (themeId !== undefined) {
    const selectedTheme = themes.find(theme => theme.id === themeId);
    if (selectedTheme) {
      return selectedTheme;
    }
  }

  if (shouldPreferDark) {
    const darkTheme = themes.find(theme => theme.variant === 'dark');
    if (darkTheme) {
      return darkTheme;
    }
  }

  const lightTheme = themes.find(theme => theme.variant === 'light');
  if (lightTheme) {
    return lightTheme;
  }

  return themes[0];
}

/**
 * Backstage {@link @backstage/core-app-api#AppThemeProvider} equivalent that listens
 * for `prefers-color-scheme` changes via `addEventListener('change')` so Auto theme
 * tracks the OS / browser appearance reliably.
 */
export function CfaAppThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const appThemeApi = useApi(appThemeApiRef);
  const themeId = useObservable(
    appThemeApi.activeThemeId$(),
    appThemeApi.getActiveThemeId(),
  );
  const shouldPreferDark = usePrefersDarkColorScheme();
  const appTheme = resolveTheme(
    themeId,
    shouldPreferDark,
    appThemeApi.getInstalledThemes(),
  );

  if (!appTheme) {
    throw new Error('App has no themes');
  }

  const Provider = appTheme.Provider;
  return <Provider>{children}</Provider>;
}
